import { downloadProductFiles } from "../api/productAsset.api";
import {
  CustomDialog,
  ToastMessage,
  WEB_COMPONENT,
} from "../models/message.model";
import {
  FileSchema,
  ProductFormKeys,
  productSchema,
} from "../models/product.model";
import QRCode from "qrcode";
import { MetaManager } from "./metaManager.util";

export const getAllProps = (component: HTMLElement) => {
  if (!component.attributes) return new Map();
  return new Map(
    Array.from(component.attributes, (attr) => [attr.name, attr.value])
  );
};

export const getRequiredComponentElements = (
  component: HTMLElement,
  componentNames: { [key: string]: string }
) => {
  const elements: Map<string, HTMLElement> = new Map();
  for (const key in componentNames) {
    const element = component.shadowRoot?.querySelector(
      `${componentNames[key]}`
    ) as HTMLElement | null;
    if (element) {
      elements.set(componentNames[key], element);
    } else {
      console.error(
        `Element ${componentNames[key]} not found in ${component.tagName}`
      );
    }
  }
  return elements;
};

export const getRequiredComponentElementFromSelector = (
  component: HTMLElement,
  componentMap: { [key: string]: { selector: string } }
) => {
  const elements: Map<string, HTMLElement> = new Map();

  for (const key in componentMap) {
    const { selector } = componentMap[key];
    if (selector) {
      const element = component.shadowRoot?.querySelector(
        selector
      ) as HTMLElement | null;
      if (element) {
        elements.set(selector, element);
      } else {
        console.error(`Element ${key} not found in ${component.tagName}`);
      }
    }
  }
  return elements;
};

export const navigateTo = (url: string) => {
  if (url) {
    window.location.href =
      window.location.origin + (url.startsWith("/") ? url : `/${url}`);
  } else {
    window.location.href = window.location.origin;
  }
};

export const scrollToBottom = () => {
  window.scrollTo({
    top: document.body.scrollHeight,
    behavior: "smooth",
  });
};

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

export const fileSizeInMB = (file: File): string => {
  const sizeInBytes = file.size;
  const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
  return `${sizeInMB} mb`;
};

export const convertBytesToMB = (size: number): string => {
  const sizeInMB = (size / (1024 * 1024)).toFixed(2);
  return `${sizeInMB} mb`;
};

export const getAllFiles = (event: Event) => {
  if (event.type === "change") {
    const target = event.target as HTMLInputElement;
    return target.files;
  }
  if (event.type === "drop") {
    const e = event as DragEvent;
    return e.dataTransfer?.files;
  }
};

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith("image/");
};

export const isDuplicateFile = (
  file: File,
  fileMap: Map<string, File>
): boolean | undefined => {
  // Avoid duplicates: check by name and size
  if (!fileMap || fileMap.size < 1) return false;
  const existingFile = fileMap.get(file.name);
  return existingFile && existingFile.size === file.size;
};

export const formDataToJson = (formData: FormData): string => {
  const obj: Record<string, any> = {};
  formData.forEach((value, key) => {
    // If key already exists, convert to array or append to array
    if (key in obj) {
      if (Array.isArray(obj[key])) {
        obj[key].push(value);
      } else {
        obj[key] = [obj[key], value];
      }
    } else {
      obj[key] = value;
    }
  });
  return JSON.stringify(obj);
};

export const getthumbnailAssetPath = (
  id: string,
  filename: string,
  width = 500,
  height = 340
) => {
  const root = process.env.API_URL;
  return `${root}/tn/custom?id=${id}&filename=${filename}&width=${width}&height=${height}`;
};

export const getDigitalAssetPath = (
  id: string,
  filename: string,
  width = 300,
  height = 300
) => {
  const root = process.env.API_URL;
  return `${root}/tn/custom?id=${id}&filename=${filename}&width=${width}&height=${height}`;
};

export const getFilenameFromPath = (path: string): string => {
  return path.split(/[/\\]/).pop() || "";
};

export const printFormData = (formData: FormData) => {
  const obj: Record<string, any> = {};
  for (const [key, value] of formData.entries()) {
    obj[key] = formData.getAll(key);
  }
  console.log(obj);
};

export const formDataLength = (formData: FormData): number => {
  let count = 0;
  for (const [key, value] of formData.entries()) {
    count = count + 1;
  }
  return count;
};

/**
 * Updates a FormData object for a given key from ProductFormKeys.
 * If the value is an array, all previous entries for that key are deleted and new ones are appended.
 * If the value is not an array, it is set directly.
 */
export function updateFormDataByKey(
  formData: FormData,
  key: string,
  value: any
): void {
  // Remove existing entries for the key
  formData.delete(key);
  // Handle arrays (e.g., tags, productPreviewLarge)
  if (Array.isArray(value)) {
    console.log(key, "is array ");
    value.forEach((item) => formData.append(key, item));
  } else if (typeof value === "object" && value !== null) {
    console.log(key, "is object ");

    // For objects, store as JSON string
    formData.set(key, JSON.stringify(value));
  } else if (value !== undefined && value !== null) {
    console.log(key, "is primitive ");

    // For primitives
    formData.set(key, value.toString());
  }
}

export function updateProductFieldsByKey(
  schema: { [key: string]: any },
  key: string,
  value: any
): { [key: string]: any } {
  // Handle nested keys for description
  if ((Object.values(ProductFormKeys.description) as string[]).includes(key)) {
    return {
      ...schema,
      description: {
        ...schema.description,
        [key]: value,
      },
    };
  }
  ``;
  // Handle nested keys for thumbnails
  if ((Object.values(ProductFormKeys.thumbnails) as string[]).includes(key)) {
    return {
      ...schema,
      thumbnails: {
        ...schema.thumbnails,
        [key]: value,
      },
    };
  }

  // Handle top-level keys
  return {
    ...schema,
    [key]: value,
  };
}

export const buildFileSchema = (file: File): FileSchema => {
  return {
    filename: file.name,
    mimetype: file.type,
    size: file.size,
    uploadedAt: new Date(),
  };
};

export const storeCartInfo = (map: Map<string, productSchema>) => {
  if (!map || map.size === 0) return;
  const serializedMap = JSON.stringify(Array.from(map.entries()));
  localStorage.setItem("cif", serializedMap);
};

export const getCartInfo = (): Map<string, productSchema> => {
  const storedMapString = localStorage.getItem("cif");
  const retrievedMap = new Map<string, productSchema>(
    JSON.parse(storedMapString || "[]")
  );
  return retrievedMap;
};

export const openOverlay = (customDialog: CustomDialog) => {
  const overlay = document.createElement("custom-overlay") as HTMLElement & {
    customOverlay?: CustomDialog;
  };
  overlay.customOverlay = customDialog;
  document.body.appendChild(overlay);
};

export const closeOverlay = (customDialog: CustomDialog) => {
  const overlay = document.querySelector("custom-overlay") as HTMLElement & {
    customOverlay?: CustomDialog;
  };
  if (overlay) {
    overlay.remove();
  }
  customDialog.actionMap.clear(); // Clear the action map to prevent memory leaks
  customDialog.textArray = []; // Clear the text array to prevent memory leaks
};

export const downloadProductAssets = async (folderId: string) => {
  try {
    const response = await downloadProductFiles(folderId);
    if (response.status !== 200) {
      throw new Error(`Error downloading file: ${response.statusText}`);
    }
    if (!response || !response.data) {
      throw new Error("No data received from the server");
    }

    const url = window.URL.createObjectURL(response.data);
    const anchorElement = document.createElement("a") as HTMLAnchorElement;
    anchorElement.href = url;
    anchorElement.style.display = "none";
    anchorElement.download = `assets.zip`;
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading the file:", error);
  }
};

export const downloadFile = async (url: string) => {
  try {
    const anchorElement = document.createElement("a") as HTMLAnchorElement;
    anchorElement.href = url;
    anchorElement.style.display = "none";
    document.body.appendChild(anchorElement);
    anchorElement.click();
    document.body.removeChild(anchorElement);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading the file:", error);
  }
};

export const hideDocumentBodyAndAppendThis = (component: WEB_COMPONENT) => {
  const cartCountElement = document.createElement(component);

  if (cartCountElement) {
    const childElements = document.body.children;

    for (const child of childElements) {
      (child as HTMLElement).style.display = "none";
    }

    document.body.appendChild(cartCountElement);
  } else {
    console.log("WEB_COMPONENT not found");
  }
};

export const clearDocumentBodyAndAppendThis = (componentName: string) => {
  const element = document.createElement(componentName);
  if (!element) {
    console.log(componentName, " component not found");
    return;
  }
  const childElements = document.body.children;

  for (const child of childElements) {
    child.remove();
  }

  document.body.appendChild(element);
};

export const generateQrCode = (data: string, element: HTMLCanvasElement) => {
  QRCode.toCanvas(element, data, function (error) {
    if (error) console.error(error);
    console.log("QR Code generated!");
  });
};

export const generateQrCode2 = (data: string, element: HTMLElement) => {
  QRCode.toDataURL(data)
    .then((url) => {
      const img = document.createElement("img");
      img.src = url;
      element.appendChild(img);
      console.log("QR Code generated as data URL!");
    })
    .catch((err) => {
      console.error(err);
    });
};

export function convertUtcToIST(isoString: string): string {
  const date = new Date(isoString); // 1. Parse the ISO string into a Date object

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // For AM/PM format
    timeZone: "Asia/Kolkata", // Specify the timezone for IST
  };

  return new Intl.DateTimeFormat("en-IN", options).format(date); // 2. Format the date into IST format using 'en-IN' locale
}

export function showToast(type: "success" | "error", text: string): void {
  const toastContainer = document.createElement("toast-component");
  if (!toastContainer) return;

  const messageObject: ToastMessage = {
    text,
    type,
  };
  (toastContainer as any).message = messageObject;

  document.body.appendChild(toastContainer);

  setTimeout(() => {
    toastContainer.setAttribute("data-show", "");
  }, 100);

  setTimeout(() => {
    toastContainer.setAttribute("data-hide", "");
    setTimeout(() => {
      toastContainer.remove();
    }, 500);
  }, 3000);
}

export const isUnderMaintenace = () => {
  const isMaintenanceEnabled = process.env.MAINTENANCE;
  if (isMaintenanceEnabled && isMaintenanceEnabled === "true")
    window.location.href = "/mn.html";
};

export function replaceHtmlStringLiterals(
  htmlString: string,
  replacements: { [key: string]: string }
): string {
  let modifiedHtml = htmlString;

  // Iterate over each replacement pair
  for (const literal in replacements) {
    if (replacements.hasOwnProperty(literal)) {
      const constantValue = replacements[literal];

      // Create a RegExp to find the string literal globally and case-insensitively (g and i flags)
      const regex = new RegExp(escapeRegExp(literal), "g");
      modifiedHtml = modifiedHtml.replaceAll(regex, constantValue); // Use replaceAll for global replacement
    }
  }

  return modifiedHtml;
}

// Helper function to escape special RegExp characters in a string
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes special characters
}

export const appendEmptyComponent = (component: HTMLElement, text: string) => {
  if (!component || !text) {
    console.log("appendEmptyComponent ", "component or element not provided");
    return;
  }

  const element = document.createElement("empty-component");
  (element as any).text = text;
  if (component.shadowRoot) {
    component.shadowRoot.appendChild(element);
  } else {
    component.appendChild(element);
  }
};

export function debounce<T extends Function>(func: T, delay: number): T {
  let timeout: ReturnType<typeof setTimeout>;

  return function (this: any, ...args: any[]) {
    const context = this;
    clearTimeout(timeout); // Clear previous timeout if still typing
    timeout = setTimeout(() => func.apply(context, args), delay); // Set new timeout
  } as unknown as T;
}

export const setMetaData = async (jsonfile: string) => {
  try {
    const homeData = await MetaManager.fetchMetaData(`/data/${jsonfile}`);
    if (homeData) {
      MetaManager.setMetaTag(homeData);
    }
  } catch (err) {
    console.error("Error loading meta data:", err);
  }
};

export const getAssetSize = (pd: productSchema): number =>
  pd.assets.reduce((x, y) => x + y.size, 0) || 0;

export const getParamFromUrl = (
  type: "query" | "path",
  param: string
): string | null => {
  if (type === "query") {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get(param);
    return id ? id : null;
  } else if (type === "path") {
    const path = window.location.pathname;
    const segments = path.split("/").filter((segment) => segment.length > 0);
    const id = segments.pop();
    // Optional: add validation to ensure the ID is a number
    if (id && !isNaN(Number(id))) {
      return id;
    }
    return null;
  }
  return null;
};

export async function copyToClipboard(textToCopy: string): Promise<void> {
  try {
    // Check if the Clipboard API is available in the browser.
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(textToCopy);
      console.log("Text successfully copied to clipboard.");
    } else {
      // Fallback for older browsers (not recommended).
      // This is generally not needed for modern Chrome and Safari.
      console.warn("Clipboard API not available. Using a fallback method.");
      // Fallback using document.execCommand (deprecated)
      // This approach is not reliable and may be removed in the future.
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      // Make sure the textarea is not visible.
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      console.log("Fallback copy successful.");
    }
  } catch (error) {
    console.error("Failed to copy text:", error);
    // You can handle the error in your application, e.g., by showing a message to the user.
    throw new Error("Failed to copy text to clipboard.");
  }
}
